/**
 * @author      Abdul Pathan
 * @date        Aug, 2024
 * @copyright   www.ibirdsservices.com
 */

import React, { useEffect, useState, useMemo, useRef } from 'react';
import WhatsAppChat from './WhatsAppChat';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { NameInitialsAvatar } from 'react-name-initials-avatar';
import jwt_decode from 'jwt-decode';
import { Modal } from 'react-bootstrap';
import { FaFilter } from 'react-icons/fa';
import DataApi from '../../../api/DataApi';
import ListViewFilterPanel from '../../layout/components/ListViewFilterPanel';
import { useListViewFilters } from '../../layout/hooks/useListViewFilters';
import { getCompleteListViewFilters } from '../../../utils/conditionBuilderUtils';
import './WhatsAppMessenger.css';

const WhatsAppMessenger = ({ socket, selectedWhatsAppSetting }) => {
  const dataApi = useMemo(() => new DataApi('watconnect_history'), []);

  const [body, setBody] = useState([]);
  const [showChat, setShowChat] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [activeFilter, setActiveFilter] = useState('recentlymessage');
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [moduleConfigs, setModuleConfigs] = useState([]);
  const [listViews, setListViews] = useState([]);
  const [selectedListView, setSelectedListView] = useState(null);
  const [isLoadingListViews, setIsLoadingListViews] = useState(false);
  const [showListViewMenu, setShowListViewMenu] = useState(false);
  const [listViewSearchText, setListViewSearchText] = useState('');
  const [activeFilters, setActiveFilters] = useState([]);
  const listViewRef = useRef(null);
  const originalListViewFiltersRef = useRef([]);
  const originalListViewLogicRef = useRef('AND');

  const {
    filterPanelOpen,
    filterPanelResourcesLoading,
    picklistOptionsByFieldId,
    externalFilters,
    externalFilterLogic,
    availableFields,
    filterValueFields,
    toggleFilterPanel,
    addExternalFilter,
    removeExternalFilter,
    updateExternalFilter,
    patchExternalFilter,
    setExternalFilters,
    setExternalFilterLogic,
    setFilterPanelOpen,
  } = useListViewFilters(activeFilter, listViews, selectedListView?.name || selectedListView?.id || '');

  const fetchConfigs = async () => {
    try {
      const configApi = new DataApi('watconnect/watconnect_module_config');
      const res = await configApi.fetchAll();
      if (res.data?.success && res.data?.data) {
        const activeConfigs = res.data.data.filter(c => c.is_active !== false);
        setModuleConfigs(activeConfigs);
      }
    } catch (error) {
      console.error('Error fetching module configs in messenger:', error);
    }
  };

  useEffect(() => {
    fetchConfigs();
  }, []);

  const tabsList = useMemo(() => {
    const fixedTabs = [
      { label: 'New Messages', value: 'recentlymessage' },
      { label: 'Unread', value: 'unread' },
    ];
    const dynamicTabs = moduleConfigs.map(c => ({
      label:
        c.plural_label ||
        c.object_label ||
        c.object_name.charAt(0).toUpperCase() + c.object_name.slice(1) + 's',
      value: c.object_name.toLowerCase(),
    }));
    return [...fixedTabs, ...dynamicTabs];
  }, [moduleConfigs]);
  const [userInfo] = useState(
    jwt_decode(localStorage.getItem('watconnect_token'))
  );
  const [bgColors] = useState([
    '#d3761f',
    '#00ad5b',
    '#debf31',
    '#239dd1',
    '#b67eb1',
    '#de242f',
  ]);
  const [brokenImages, setBrokenImages] = useState([]);
  const [unreadMsgCount, setUnreadMsgCount] = useState([]);
  const profileImage = '/public/' + userInfo.tenantcode + '/users';

  let colorIndex = 0;
  const LIMIT = 50;

  const getRawVal = (val) => {
    if (val === null || val === undefined) return '';
    if (Array.isArray(val)) {
      return val.map(v => getRawVal(v)).filter(Boolean).join(',');
    }
    if (typeof val === 'object') {
      if (val.value !== undefined && val.value !== null) {
        return String(val.value);
      }
      if (val.displayValue !== undefined && val.displayValue !== null) {
        return String(val.displayValue);
      }
      if (val.label !== undefined && val.label !== null) {
        return String(val.label);
      }
      return '';
    }
    return String(val);
  };

  const getDisplayVal = (val) => {
    if (val === null || val === undefined) return '';
    if (Array.isArray(val)) {
      return val.map(v => getDisplayVal(v)).filter(Boolean).join(',');
    }
    if (typeof val === 'object') {
      if (val.displayValue !== undefined && val.displayValue !== null) {
        return String(val.displayValue);
      }
      if (val.label !== undefined && val.label !== null) {
        return String(val.label);
      }
      if (val.value !== undefined && val.value !== null) {
        return String(val.value);
      }
      return '';
    }
    return String(val);
  };

  const getVal = (val) => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'object') {
      if (val.displayValue !== undefined && val.displayValue !== null) {
        return String(val.displayValue);
      }
      if (val.value !== undefined && val.value !== null) {
        return String(val.value);
      }
      if (val.label !== undefined && val.label !== null) {
        return String(val.label);
      }
      return '';
    }
    return String(val);
  };

  const matchesFilters = (rec, filters) => {
    if (!Array.isArray(filters) || filters.length === 0) return true;

    return filters.every(f => {
      const fieldName = f.field || f.fieldName || f.apiName || '';
      if (!fieldName) return true;

      const op = (f.operator || 'equals').toLowerCase();
      const fieldKey = Object.keys(rec).find(k => k.toLowerCase() === fieldName.toLowerCase());

      if (op === 'is null' || op === 'is_null') {
        if (!fieldKey) return true;
        const raw = getRawVal(rec[fieldKey]).trim();
        const disp = getDisplayVal(rec[fieldKey]).trim();
        return !raw && !disp;
      }

      if (op === 'is not null' || op === 'is_not_null') {
        if (!fieldKey) return false;
        const raw = getRawVal(rec[fieldKey]).trim();
        const disp = getDisplayVal(rec[fieldKey]).trim();
        return raw.length > 0 || disp.length > 0;
      }

      if (f.value === undefined || f.value === null || f.value === '') {
        return true;
      }

      // If the record doesn't contain this field key in the response payload, do not discard it client-side
      if (!fieldKey) return true;

      const fieldRawValStr = getRawVal(rec[fieldKey]).toLowerCase().trim();
      const fieldDispValStr = getDisplayVal(rec[fieldKey]).toLowerCase().trim();

      const filterRawValStr = getRawVal(f.value).toLowerCase().trim();
      const filterDispValStr = getDisplayVal(f.value || f.displayValue).toLowerCase().trim();

      const checkMatch = (valStr, targetStr) => {
        if (!targetStr) return false;
        if (op === 'equals' || op === '=') {
          return valStr === targetStr;
        }
        if (op === 'not_equals' || op === 'not equals' || op === '!=') {
          return valStr !== targetStr;
        }
        if (op === 'contains') {
          return valStr.includes(targetStr);
        }
        if (op === 'in') {
          const inValues = targetStr.split(',').map(v => v.trim()).filter(Boolean);
          return inValues.includes(valStr);
        }
        if (op === 'not in' || op === 'not_in') {
          const inValues = targetStr.split(',').map(v => v.trim()).filter(Boolean);
          return !inValues.includes(valStr);
        }
        return valStr.includes(targetStr);
      };

      const matchRaw = checkMatch(fieldRawValStr, filterRawValStr) || (filterDispValStr ? checkMatch(fieldRawValStr, filterDispValStr) : false);
      const matchDisp = checkMatch(fieldDispValStr, filterRawValStr) || (filterDispValStr ? checkMatch(fieldDispValStr, filterDispValStr) : false);

      return matchRaw || matchDisp;
    });
  };

  const fetchListViewsForFilter = async (filterObjectName) => {
    try {
      setIsLoadingListViews(true);
      const listViewApi = new DataApi(`objectconfig/listviews/${filterObjectName}`);
      const response = await listViewApi.fetchAll('platform=web');

      if (response?.data?.success && response.data.data && response.data.data.length > 0) {
        const fetchedViews = response.data.data;
        setListViews(fetchedViews);
        const defaultId = response.data.userDefaultListViewId;
        const initialView = fetchedViews.find(v => v.id === defaultId || v.name === defaultId) || fetchedViews[0];
        setSelectedListView(initialView);
        const initialFilters = initialView?.config?.filters || [];
        originalListViewFiltersRef.current = JSON.parse(JSON.stringify(initialFilters));
        originalListViewLogicRef.current = initialView?.config?.logic || 'AND';
        setExternalFilters(initialFilters);
        setActiveFilters(initialFilters);
        return initialView;
      } else {
        setListViews([]);
        setSelectedListView(null);
        originalListViewFiltersRef.current = [];
        originalListViewLogicRef.current = 'AND';
        setExternalFilters([]);
        setActiveFilters([]);
        return null;
      }
    } catch (error) {
      console.error('Error fetching list views for messenger:', error);
      setListViews([]);
      setSelectedListView(null);
      originalListViewFiltersRef.current = [];
      originalListViewLogicRef.current = 'AND';
      setExternalFilters([]);
      setActiveFilters([]);
      return null;
    } finally {
      setIsLoadingListViews(false);
    }
  };

  const fetchData = async (currentOffset = 0, append = false, currentListView = selectedListView, filtersToApply = activeFilters) => {
    const isSpecialTab = activeFilter === 'recentlymessage' || activeFilter === 'unread';

    if (!isSpecialTab && currentListView && (currentListView.name || currentListView.id)) {
      const listViewName = currentListView.name || currentListView.id;
      try {
        const listviewApi = new DataApi(`object/listview/${activeFilter}/${listViewName}`);
        let res = await listviewApi.fetchAll('limit=all&platform=web&viewType=list');

        const recordsList = res.data?.data?.records || res.data?.records || (Array.isArray(res.data?.data) ? res.data.data : []);

        const filteredRecordsList = (Array.isArray(filtersToApply) && filtersToApply.length > 0)
          ? recordsList.filter(rec => matchesFilters(rec, filtersToApply))
          : recordsList;

        if (filteredRecordsList) {
          const activeConfig = moduleConfigs.find(
            c => c.object_name.toLowerCase() === activeFilter.toLowerCase()
          );
          const phoneFieldName = (activeConfig?.field_name || 'phone').toLowerCase();

          const formattedRecords = filteredRecordsList
            .map(rec => {
              const recId = getVal(rec.id);
              const firstName = getVal(rec.first_name || rec.firstname);
              const lastName = getVal(rec.last_name || rec.lastname);
              const fullNameCombined = (firstName && lastName) ? `${firstName} ${lastName}` : '';

              const name = getVal(rec.name) ||
                fullNameCombined ||
                getVal(rec.full_name) ||
                getVal(rec.contactname) ||
                getVal(rec.title) ||
                'Unknown';

              const phoneVal = getVal(rec[activeConfig?.field_name]) ||
                getVal(rec[phoneFieldName]) ||
                getVal(rec.whatsapp_number) ||
                getVal(rec.phone) ||
                getVal(rec.mobile) ||
                '';

              return {
                id: recId,
                name: name,
                contactname: name,
                whatsapp_number: phoneVal,
              };
            })
            .filter(r => {
              const cleanPhone = String(r.whatsapp_number || '').replace(/\D/g, '');
              return cleanPhone.length >= 7;
            });

          // Filter by search text if provided
          const filteredBySearch = searchText
            ? formattedRecords.filter(r =>
              (r.name || '').toLowerCase().includes(searchText.toLowerCase()) ||
              (r.whatsapp_number || '').includes(searchText)
            )
            : formattedRecords;

          if (append) {
            const nextBatch = filteredBySearch.slice(currentOffset, currentOffset + LIMIT);
            setBody(prev => [...prev, ...nextBatch]);
          } else {
            const initialBatch = filteredBySearch.slice(0, LIMIT);
            setBody(initialBatch);
          }
          setHasMore(filteredBySearch.length > currentOffset + LIMIT);
          return;
        }
      } catch (err) {
        console.error('Error fetching listview records in messenger:', err);
      }
    }

    // Standard fallback to watconnect_history
    const activeConfig = moduleConfigs.find(
      c => c.object_name.toLowerCase() === activeFilter.toLowerCase()
    );
    const phoneParam = activeConfig
      ? `&phone=${encodeURIComponent(activeConfig.field_name)}`
      : '';
    const listViewParam = (!isSpecialTab && currentListView?.name)
      ? `&listView=${encodeURIComponent(currentListView.name)}`
      : '';

    let result = await dataApi.fetchAll(
      `filter=${activeFilter}&search=${encodeURIComponent(searchText || '')}&businessNumber=${encodeURIComponent(selectedWhatsAppSetting || '')}&limit=${LIMIT}&offset=${currentOffset}${phoneParam}${listViewParam}`
    );

    if (result?.data?.success) {
      const newRecords = result?.data?.records || [];
      if (append) {
        setBody(prev => [...prev, ...newRecords]);
      } else {
        setBody(newRecords);
      }
      setHasMore(newRecords.length >= LIMIT);
    } else {
      if (!append) {
        setBody([]);
      }
      setHasMore(false);
    }
  };

  // Create refs to store the latest fetch functions so socket listeners don't capture stale state
  const fetchDataRef = useRef(null);
  const getUnreadMsgCountsRef = useRef(null);

  useEffect(() => {
    fetchDataRef.current = fetchData;
    getUnreadMsgCountsRef.current = getUnreadMsgCounts;
  });

  // SOCKET listener
  useEffect(() => {
    if (!socket) return;
    const handleMessage = () => {
      setOffset(0);
      setHasMore(true);
      if (fetchDataRef.current) {
        fetchDataRef.current(0, false, selectedListView);
      }
      if (getUnreadMsgCountsRef.current) {
        getUnreadMsgCountsRef.current();
      }
    };

    socket.on('receivedwhatsappmessage', handleMessage);
    socket.on('outgoingwhatsappmessage', handleMessage);
    socket.on('whatsapp_statuses', handleMessage);

    return () => {
      socket.off('receivedwhatsappmessage', handleMessage);
      socket.off('outgoingwhatsappmessage', handleMessage);
      socket.off('whatsapp_statuses', handleMessage);
    };
  }, [socket, selectedWhatsAppSetting, selectedListView]);

  // Handle click outside popover menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (listViewRef.current && !listViewRef.current.contains(event.target)) {
        setShowListViewMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredListViews = useMemo(() => {
    if (!listViewSearchText) return listViews;
    return listViews.filter(lv =>
      (lv.label || lv.name || '').toLowerCase().includes(listViewSearchText.toLowerCase())
    );
  }, [listViews, listViewSearchText]);

  // FETCH logic when filter or settings change
  useEffect(() => {
    setOffset(0);
    setHasMore(true);
    setShowListViewMenu(false);
    setListViewSearchText('');

    const isSpecialTab = activeFilter === 'recentlymessage' || activeFilter === 'unread';

    if (!isSpecialTab) {
      fetchListViewsForFilter(activeFilter).then(initialView => {
        fetchData(0, false, initialView);
      });
    } else {
      setListViews([]);
      setSelectedListView(null);
      fetchData(0, false, null);
    }

    getUnreadMsgCounts(selectedWhatsAppSetting);
  }, [activeFilter, selectedWhatsAppSetting]);

  // FETCH logic when search text changes
  useEffect(() => {
    fetchData(0, false, selectedListView);
  }, [searchText]);

  const getUnreadMsgCounts = async () => {
    try {
      const dataApi = new DataApi('watconnect_history/unread_count');

      let result = await dataApi.fetchAll(
        `whatsapp_setting_number=${encodeURIComponent(selectedWhatsAppSetting || '')}`
      );

      if (result && result.data && result.data.success && result.data.records) {
        const messages = result.data.records;
        setUnreadMsgCount(messages);
      } else {
        console.log('No unread messages found in WhatsAppMessenger');
        setUnreadMsgCount([]);
      }
    } catch (error) {
      console.error(
        'Error fetching unread messages in WhatsAppMessenger:',
        error
      );
      setUnreadMsgCount([]);
    }
  };

  const markMessagesAsRead = async whatsappNumber => {
    try {
      const dataApi = new DataApi('watconnect_history/mark_as_read');
      const result = await dataApi.post(
        `?whatsapp_setting_number=${encodeURIComponent(selectedWhatsAppSetting || '')}`,
        { whatsapp_number: whatsappNumber }
      );

      console.log('Mark as read response in WhatsAppMessenger:', result);

      if (result && result.data && result.data.success) {
        console.log(
          'Messages marked as read successfully in WhatsAppMessenger'
        );
        // Refresh unread counts after marking as read
        getUnreadMsgCounts();
        return true;
      } else {
        console.log('Failed to mark messages as read in WhatsAppMessenger');
        return false;
      }
    } catch (error) {
      console.error(
        'Error marking messages as read in WhatsAppMessenger:',
        error
      );
      return false;
    }
  };

  const handleLoadMore = () => {
    const nextOffset = offset + LIMIT;
    setOffset(nextOffset);
    fetchData(nextOffset, true);
  };

  const handleUserClick = (item, unreadCount) => {
    setShowChat(true);
    setSelectedUser(item);
    if (item.whatsapp_number && unreadCount > 0) {
      const fullNumber = item.whatsapp_number?.startsWith('+')
        ? item.whatsapp_number
        : `${item.whatsapp_number}`;
      markMessagesAsRead(fullNumber);
    }
  };

  const getAvatarColor = () => {
    const color = bgColors[colorIndex % bgColors.length];
    colorIndex++;
    return color;
  };

  const getUnreadCount = item => {
    const fullNumber = item.whatsapp_number?.startsWith('+')
      ? item.whatsapp_number
      : `${item.whatsapp_number}`;
    const unread = unreadMsgCount.find(
      unread => unread.whatsapp_number === fullNumber
    );
    return unread?.unread_msg_count || 0;
  };

  return (
    <>
      <div className='wa-container'>
        <div className='wa-wrapper'>
          {/* LEFT SIDEBAR */}
          <div className='wa-sidebar'>
            {/* Header */}
            <div className='wa-sidebar-header'>
              <NameInitialsAvatar
                size='40px'
                textSize='16px'
                bgColor='#6B7C85'
                borderWidth='0px'
                textColor='#fff'
                name={userInfo?.username || userInfo?.name || 'User'}
              />
            </div>

            {/* Search */}
            <div className='wa-search'>
              <i className='fa-solid fa-magnifying-glass'></i>
              <input
                type='text'
                placeholder='Search or start new chat'
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
              />
            </div>

            {/* Tabs */}
            <div className='wa-tabs'>
              {tabsList.map(filter => (
                <button
                  key={filter.value}
                  className={`wa-tab ${activeFilter === filter.value ? 'active' : ''
                    }`}
                  onClick={() => setActiveFilter(filter.value)}
                >
                  {filter.label}
                </button>
              ))}
            </div>

            {/* Sleek WhatsApp List View Filter Bar & Popover Menu */}
            {activeFilter !== 'recentlymessage' && activeFilter !== 'unread' && (
              <div className='wa-listview-bar' ref={listViewRef}>
                <div className='wa-listview-pill-wrapper'>
                  <button
                    type='button'
                    className={`wa-filter-btn ${activeFilters.length > 0 ? 'active' : ''}`}
                    onClick={toggleFilterPanel}
                    title='Filter Records'
                  >
                    <i className='fa-solid fa-filter'></i>
                    {activeFilters.length > 0 && (
                      <span className='wa-filter-badge'>{activeFilters.length}</span>
                    )}
                  </button>
                  <button
                    type='button'
                    className={`wa-listview-pill ${showListViewMenu ? 'active' : ''}`}
                    disabled={isLoadingListViews || listViews.length === 0}
                    onClick={() => setShowListViewMenu(prev => !prev)}
                  >
                    <span className='wa-pill-icon'>
                      <i className='fa-solid fa-sliders me-1'></i>
                    </span>
                    <span className='wa-pill-label'>
                      {selectedListView ? (selectedListView.label || selectedListView.name) : 'Select List View'}
                    </span>
                    {isLoadingListViews ? (
                      <i className='fa fa-spinner fa-spin ms-2 wa-spin-icon'></i>
                    ) : (
                      <span className='wa-pill-count me-1'>{body.length}</span>
                    )}
                    <i className={`fa-solid fa-chevron-down ms-1 wa-chevron ${showListViewMenu ? 'rotated' : ''}`}></i>
                  </button>
                </div>

                {/* Popover Dropdown Menu */}
                {showListViewMenu && (
                  <div className='wa-listview-popover'>
                    <div className='wa-popover-header'>
                      <span className='wa-popover-title'>
                        <i className='fa-solid fa-list-ul me-1.5'></i>List Views
                      </span>
                      <span className='wa-popover-badge'>
                        {listViews.length} views
                      </span>
                    </div>

                    {listViews.length > 5 && (
                      <div className='wa-popover-search'>
                        <i className='fa-solid fa-magnifying-glass'></i>
                        <input
                          type='text'
                          placeholder='Search views...'
                          value={listViewSearchText}
                          onChange={e => setListViewSearchText(e.target.value)}
                        />
                        {listViewSearchText && (
                          <i
                            className='fa-solid fa-xmark text-muted cursor-pointer'
                            onClick={() => setListViewSearchText('')}
                          ></i>
                        )}
                      </div>
                    )}

                    <div className='wa-popover-list'>
                      {filteredListViews.length === 0 ? (
                        <div className='wa-popover-empty'>No matching list views</div>
                      ) : (
                        filteredListViews.map(lv => {
                          const isSelected = selectedListView?.id === lv.id || selectedListView?.name === lv.name;
                          return (
                            <div
                              key={lv.id || lv.name}
                              className={`wa-popover-item ${isSelected ? 'selected' : ''}`}
                              onClick={() => {
                                setSelectedListView(lv);
                                setShowListViewMenu(false);
                                setListViewSearchText('');
                                const lvFilters = lv?.config?.filters || [];
                                originalListViewFiltersRef.current = JSON.parse(JSON.stringify(lvFilters));
                                originalListViewLogicRef.current = lv?.config?.logic || 'AND';
                                setExternalFilters(lvFilters);
                                setActiveFilters(lvFilters);
                                fetchData(0, false, lv, lvFilters);
                              }}
                            >
                              <div className='wa-item-info'>
                                <span className='wa-item-name'>{lv.label || lv.name}</span>
                              </div>
                              {isSelected && (
                                <i className='fa-solid fa-circle-check wa-check-icon'></i>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Contacts */}
            <div className='wa-contact-list'>
              {body.length > 0 ? (
                <>
                  {body.map(item => {
                    const unreadCount = getUnreadCount(item);
                    const isActive = selectedUser?.id === item.id;

                    return (
                      <div
                        key={item.id}
                        className={`wa-contact ${isActive ? 'active' : ''}`}
                        onClick={() => handleUserClick(item, unreadCount)}
                      >
                        <div className='wa-avatar'>
                          <NameInitialsAvatar
                            size='40px'
                            textSize='16px'
                            bgColor={getAvatarColor()}
                            borderWidth='0px'
                            textColor='#fff'
                            name={
                              item?.name ||
                              item?.contactname ||
                              item?.whatsapp_number ||
                              '?'
                            }
                          />
                        </div>

                        <div className='wa-contact-info'>
                          <div className='wa-contact-top'>
                            <span className='wa-contact-name'>
                              {item?.name || item?.contactname}
                            </span>
                          </div>
                          <div className='wa-contact-bottom'>
                            <span className='wa-contact-number'>
                              {item?.whatsapp_number}
                            </span>
                            {unreadCount > 0 && (
                              <span className='wa-badge float-end'>
                                {unreadCount}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {hasMore && (
                    <div className='wa-load-more-container'>
                      <button
                        className='wa-load-more-btn'
                        onClick={handleLoadMore}
                      >
                        Load More
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className='wa-empty'>No conversations found</div>
              )}
            </div>
          </div>

          {/* RIGHT CHAT AREA */}
          <div className='wa-chat'>
            {showChat && selectedUser ? (
              <WhatsAppChat
                isOpen={showChat}
                onToggle={() => setShowChat(false)}
                onClose={() => setShowChat(false)}
                recipientName={selectedUser?.name}
                recipientPhone={selectedUser?.whatsapp_number}
                socket={socket}
                selectedWhatsAppSetting={selectedWhatsAppSetting}
              />
            ) : (
              <div className='wa-chat-empty'>
                <div className='wa-logo'>
                  <i className='fa-brands fa-whatsapp'></i>
                </div>
                <h2>WhatsApp Web</h2>
                <p>
                  Send and receive messages without keeping your phone online.
                  <br />
                  Use WhatsApp on up to 4 linked devices and 1 phone at the same
                  time.
                </p>
                <div className='wa-encryption'>
                  <i className='fa-solid fa-lock'></i> End-to-end encrypted
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <Modal show={filterPanelOpen} onHide={() => setFilterPanelOpen(false)} centered size="lg">
        <Modal.Header closeButton style={{ borderBottom: '1px solid #e5e5e5' }}>
          <Modal.Title className="d-flex align-items-center gap-2 fs-6 fw-semibold text-primary mb-0">
            <FaFilter className="text-primary" />
            Quick filters
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          <ListViewFilterPanel
            showTitleBar={false}
            objectName={activeFilter}
            externalFilters={externalFilters}
            externalFilterLogic={externalFilterLogic}
            availableFields={availableFields}
            filterValueFields={filterValueFields}
            resourcesLoading={filterPanelResourcesLoading}
            picklistOptionsByFieldId={picklistOptionsByFieldId}
            onClose={() => setFilterPanelOpen(false)}
            onAddFilter={addExternalFilter}
            onRemoveFilter={removeExternalFilter}
            onUpdateFilter={updateExternalFilter}
            onPatchFilter={patchExternalFilter}
            onUpdateLogic={setExternalFilterLogic}
            onApplyFilters={async () => {
              const complete = getCompleteListViewFilters(externalFilters);
              setActiveFilters(complete);
              setFilterPanelOpen(false);

              if (selectedListView && activeFilter) {
                try {
                  const updatedConfig = {
                    ...selectedListView.config,
                    filters: complete,
                    logic: externalFilterLogic || 'AND',
                  };
                  const configApi = new DataApi(`objectconfig/listviews/${activeFilter}`);
                  const updateRes = await configApi.update({ config: updatedConfig }, selectedListView.id);

                  if (updateRes?.data?.success && updateRes.data.data) {
                    const updatedListView = updateRes.data.data;
                    setSelectedListView(updatedListView);
                    setListViews(prev => prev.map(lv => lv.id === updatedListView.id ? updatedListView : lv));
                    await fetchData(0, false, updatedListView, complete);
                    return;
                  }
                } catch (err) {
                  console.error('Error updating list view filters in messenger:', err);
                }
              }
              await fetchData(0, false, selectedListView, complete);
            }}
            onClearFilters={async () => {
              setExternalFilters([]);
              setActiveFilters([]);
              setFilterPanelOpen(false);

              if (selectedListView && activeFilter) {
                try {
                  const updatedConfig = {
                    ...selectedListView.config,
                    filters: [],
                    logic: 'AND',
                  };
                  const configApi = new DataApi(`objectconfig/listviews/${activeFilter}`);
                  const updateRes = await configApi.update({ config: updatedConfig }, selectedListView.id);

                  if (updateRes?.data?.success && updateRes.data.data) {
                    const updatedListView = updateRes.data.data;
                    setSelectedListView(updatedListView);
                    setListViews(prev => prev.map(lv => lv.id === updatedListView.id ? updatedListView : lv));
                    await fetchData(0, false, updatedListView, []);
                    return;
                  }
                } catch (err) {
                  console.error('Error clearing list view filters in messenger:', err);
                }
              }
              await fetchData(0, false, selectedListView, []);
            }}
          />
        </Modal.Body>
      </Modal>
      <ToastContainer />
    </>
  );
};

export default WhatsAppMessenger;
